import { useEffect, useRef } from "react";

/**
 * Leva a pessoa até o formulário que o clique acabou de abrir.
 *
 * Nasceu de um defeito de tela, não de estética: nos dois catálogos o formulário de edição fica
 * fora da dobra — acima da tabela num, abaixo dela no outro — e clicar "Editar" numa linha lá
 * embaixo preenchia um formulário que ninguém via. Não há erro, não há aviso, e a leitura
 * inocente de quem clicou é "o botão não funcionou".
 *
 * Rolar sozinho não resolve: quem clicou "Editar" quer digitar. O foco vai para o primeiro
 * controle que aceita digitação, o que de quebra encurta o caminho de quem navega por teclado ou
 * leitor de tela e teria de percorrer a tabela inteira de volta para chegar ao campo.
 *
 * Dois detalhes que parecem enfeite e não são:
 *
 * * `preventScroll` no foco — focar um elemento fora da tela o traz de um salto e cancelaria a
 *   rolagem suave que acabou de começar;
 * * `prefers-reduced-motion` — a rolagem longa é justamente o tipo de movimento que incomoda
 *   quem pediu para o sistema não animar.
 *
 * A margem de respiro fica no CSS de quem chama (`scroll-mt-*`), porque só a tela sabe o que
 * mais precisa caber acima do formulário.
 */
export function levarAoFormulario(moldura: HTMLElement | null): void {
  if (!moldura) return;

  const semAnimacao = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  moldura.scrollIntoView({ behavior: semAnimacao ? "auto" : "smooth", block: "start" });

  const primeiro = moldura.querySelector<HTMLElement>(
    "input:not([disabled]):not([type='hidden']), textarea:not([disabled]), select:not([disabled])",
  );
  primeiro?.focus({ preventScroll: true });
}

/**
 * Foco de uma confirmação que aparece na própria tela, sem modal: ao abrir, o foco vai para ela,
 * e quem usa leitor de tela ouve a pergunta em vez de ficar no botão que a abriu; ao fechar (ou
 * desmontar), o foco volta para o controle que a abriu, se ele ainda estiver na tela. Quem chama
 * põe a ref no bloco da pergunta, com `tabIndex={-1}`, e passa o que identifica a pergunta (vazio
 * quando fechada): se ela muda com a confirmação aberta, o foco vai de novo para a pergunta nova.
 * O controle que abriu a pergunta vem de quem chama (o `currentTarget` do evento): o foco do
 * navegador nem sempre está nele — um `<select>` trocado por script, por exemplo, não o recebe.
 */
export function useFocoDaConfirmacao<T extends HTMLElement>(
  pergunta: string | null,
  origem: HTMLElement | null,
) {
  const bloco = useRef<T>(null);
  useEffect(() => {
    if (!pergunta) return;
    const alvo = bloco.current;
    alvo?.focus();
    return () => {
      // Só devolve o foco que ficou órfão: se a pessoa já foi para outro controle, fica lá.
      const atual = document.activeElement;
      const orfao = !atual || atual === document.body || Boolean(alvo?.contains(atual));
      if (orfao && origem?.isConnected) origem.focus();
    };
  }, [pergunta, origem]);
  return bloco;
}
